import React, { useEffect, useMemo } from "react";
import {
  Box,
  CircularProgress,
  Typography,
  IconButton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Divider,
  Card,
  CardContent,
} from "@mui/material";
import {
  ModeEditOutlineTwoTone,
  AttachMoney,
  Discount,
  AccountBalance,
  Cancel,
  CheckCircle,
  InfoOutlined,
} from "@mui/icons-material";
import { toast } from "react-toastify";

import AppTable from "components/Shared/AppTable";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import { useShipmentDetailContext } from "contexts/ShipmentDetailContext";
import { useAuth, useTablePagination } from "hooks";
import { useLazyListShipmentPricesQuery } from "store/services/shipmentApi";
import AppTableDateTime from "components/Shared/AppTableDateTime";
import { Link, useNavigate } from "react-router-dom";
import { PrintMenuCompact } from "./ShipmentPricingCompact";

const ShipmentPricingList = ({ setSelectedPrice }) => {
  const navigate = useNavigate();

  const { isSystemAdmin, isAccountAdmin } = useAuth();
  const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
    useTablePagination();
  const { ShipmentDetail } = useShipmentDetailContext();

  const columns = [
    {
      name: "Fee Item",
      selector: (row) => row?.feeItem?.name || "",
      cell: (row) => row?.feeItem?.name || "N/A",
      sortable: true,
    },
    {
      name: "Type",
      selector: (row) => row?.chargeType || "",
      cell: (row) => row?.chargeType || "N/A",
      sortable: true,
    },
    {
      name: "Amount",
      selector: (row) => row?.charge || 0,
      cell: (row) => {
        if (row?.charge === undefined || row?.charge === null) return "N/A";
        const isNegative =
          row?.charge < 0 ||
          row?.chargeType === "Discount" ||
          row?.feeItem?.name === "COD Adjustment Charge";
        return `${isNegative ? "- " : ""}Rs. ${Math.abs(row.charge).toFixed(2)}`;
      },
      sortable: true,
    },
    {
      name: "Payer",
      selector: (row) => row?.payer || "",
      cell: (row) => row?.payer || "N/A",
      sortable: true,
    },
    {
      name: "Paid At",
      selector: (row) => row?.paymentLocation || "",
      cell: (row) => row?.paymentLocation || "N/A",
      sortable: true,
    },
    {
      name: "User",
      selector: (row) => row?.user?.name || "",
      cell: (row) => row?.user?.name || "N/A",
      sortable: true,
    },
    {
      name: "Updated By",
      selector: (row) => row?.updatedBy?.name || "",
      cell: (row) => row?.updatedBy?.name || "N/A",
      sortable: true,
    },
    {
      name: "Responsible",
      selector: (row) => row?.responsibleUser?.name || "",
      cell: (row) => row?.responsibleUser?.name || "N/A",
      sortable: true,
    },

    {
      name: "COD Remitted",
      selector: (row) => row?.remitStatus || "",
      cell: (row) =>
        row?.chargeType === "Cash on Delivery" ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {row?.remitStatus === "Transferred" ? (
              <CheckCircle
                sx={{
                  color: "#2e7d32",
                  fontSize: 20,
                }}
                aria-label="Transferred"
              />
            ) : (
              <Cancel
                sx={{
                  color: "#c62828",
                  fontSize: 20,
                }}
                aria-label="Not Transferred"
              />
            )}
          </Box>
        ) : (
          "N/A"
        ),
      sortable: true,
      width: "100px",
    },
    {
      name: "Remit Source",
      selector: (row) => row?.remitSource || "",
      cell: (row) => {
        if (row?.chargeType !== "Cash on Delivery") return "N/A";
        if (!row?.remitSource || !row?.remitSourceRef) return "N/A";

        return (
          <Link
            to={`/app/crm/cod-transfer/${row.remitSourceRef}/detail`}
            className="cursor-pointer text-blue-600 transition-colors duration-200 hover:text-blue-800 hover:underline dark:text-blue-400 dark:hover:text-blue-300"
            aria-label={`View details for ${row.remitSource}`}
          >
            {row.remitSource}
          </Link>
        );
      },
      sortable: true,
    },
    {
      name: "Source",
      selector: (row) => row?.source || "",
      cell: (row) => {
        if (!row?.source || !row?.sourceRef) return "N/A";

        let path = "";
        if (row.source.startsWith("COC-")) {
          path = "coc-collection";
        } else if (row.source.startsWith("COD-")) {
          path = "cod-transfer";
        } else if (row.source.startsWith("DC-")) {
          path = "delivery-collection";
        } else if (row.source.startsWith("PP-")) {
          path = "prepayment";
        } else {
          return row.source; // fallback: just display the source without a link
        }

        return (
          <Link
            to={`/app/crm/${path}/${row.sourceRef}/detail`}
            className="cursor-pointer text-blue-600 transition-colors duration-200 hover:text-blue-800 hover:underline dark:text-blue-400 dark:hover:text-blue-300"
            aria-label={`View details for ${row.source}`}
          >
            {row.source}
          </Link>
        );
      },
      sortable: true,
    },
    {
      name: "Status",
      selector: (row) =>
        (row?.chargeType === "Charge" || row?.chargeType === "Cash on Delivery"
          ? row?.paymentStatus
          : row?.discountStatus) || "",
      cell: (row) => {
        const status =
          row?.chargeType === "Charge" || row?.chargeType === "Cash on Delivery"
            ? row?.paymentStatus
            : row?.discountStatus;
        const isPositiveStatus =
          status === "Paid" || status === "Applied" || status === "Collected";

        return (
          <Chip
            label={status || "N/A"}
            sx={{
              backgroundColor: isPositiveStatus ? "#e6f4ea" : "#ffeaea",
              color: isPositiveStatus ? "#2e7d32" : "#c62828",
              fontWeight: "bold",
              height: 20,
              borderRadius: "12px",
              padding: "0",
              "& .MuiChip-label": {
                fontSize: "0.625rem",
                padding: "2px 6px",
                lineHeight: 1,
              },
            }}
          />
        );
      },
      sortable: true,
    },

    {
      name: "Updated At",
      selector: (row) => row?.updatedAt || "",
      cell: (row) => <AppTableDateTime date={row?.updatedAt} />,
      sortable: true,
    },
    {
      name: "Actions",
      cell: (row) => (
        <Box sx={{ display: "flex", gap: 1, position: "relative" }}>
          {ShipmentDetail?.status !== "Delivered" &&
            (isAccountAdmin ||
              isSystemAdmin) && (
              <IconButton
                size="small"
                aria-label="Edit Price"
                onClick={() => {
                  if (!row?._id) {
                    toast.error("Cannot edit price: Missing price ID");
                    return;
                  }
                  setSelectedPrice({
                    _id: row?._id,
                    feeItem: row?.feeItem?._id || row?.feeItem || "",
                    responsibleUser:
                      row?.responsibleUser?._id || row?.responsibleUser || null,
                    charge: Math.abs(row?.charge || 0),
                    chargeType: row?.chargeType || "Charge",
                    payer: row?.payer || "Sender",
                    paymentLocation:
                      row?.paymentLocation ||
                      (row?.payer === "Receiver" ||
                        row?.chargeType === "Cash on Delivery"
                        ? "Destination"
                        : "Source"),
                    paymentStatus: row?.paymentStatus || "Not Paid",
                    discountStatus: row?.discountStatus || "Not Applied",
                    source: row?.source || "",
                    sourceRef: row?.sourceRef || "",
                    user: row?.user || { name: "" },
                  });
                }}
                disabled={
                  row?.paymentStatus === "Paid" ||
                  row?.discountStatus === "Applied" ||
                  row?.paymentStatus === "Collected" ||
                  row?.paymentStatus === "Refunded" ||
                  ShipmentDetail?.isActive === false
                }
              >
                <ModeEditOutlineTwoTone />
              </IconButton>
            )}
          <PrintMenuCompact row={row} />
          <IconButton
            size="small"
            aria-label="View Price Details"
            onClick={() =>
              navigate(`/app/crm/shipment/price/${row._id}/detail`, {
                state: { shipmentId: ShipmentDetail._id },
              })
            }
          >
            <InfoOutlined />
          </IconButton>
        </Box>
      ),
      ignoreRowClick: true,
      right: true,
    },
  ];

  // RTK Query
  const [fetchPrices, { data: pricesRes, isLoading, isFetching }] =
    useLazyListShipmentPricesQuery();

  useEffect(() => {
    if (!ShipmentDetail?._id) {
      return;
    }
    fetchPrices({
      shipmentId: ShipmentDetail._id,
      page: currentPage,
      limit: perPage,
    });
  }, [ShipmentDetail?._id, currentPage, perPage, fetchPrices]);

  // Calculate Net Summary: ONLY Charge type is included (normal charges and COD adjustment charge).
  // Cash on Delivery is NOT a charge and is excluded from Total Charges.
  const netSummary = useMemo(() => {
    const prices = Array.isArray(pricesRes?.data) ? pricesRes.data : [];

    if (!prices.length && pricesRes) {
      return {
        totalCharges: pricesRes?.totalCharges ?? 0,
        totalDiscounts: pricesRes?.totalDiscounts ?? 0,
        remainingAmount: pricesRes?.remainingShipmentAmount ?? 0,
      };
    }

    let calculatedCharges = 0;
    let calculatedDiscounts = 0;
    let calculatedPaid = 0;

    prices.forEach((row) => {
      const charge = Number(row?.charge || 0);
      const isCodAdjustment =
        row?.feeItem?.name === "COD Adjustment Charge" ||
        row?.source === "ShipmentRefund";

      if (row?.chargeType === "Charge") {
        if (isCodAdjustment) {
          calculatedCharges -= Math.abs(charge);
        } else if (charge < 0) {
          calculatedCharges += charge;
        } else {
          calculatedCharges += charge;
        }

        if (
          (row?.paymentStatus === "Paid" || row?.paymentStatus === "Collected") &&
          !isCodAdjustment &&
          charge > 0
        ) {
          calculatedPaid += charge;
        }
      } else if (
        row?.chargeType === "Discount" &&
        row?.discountStatus === "Applied"
      ) {
        calculatedDiscounts += Math.abs(charge);
      }
    });

    const remaining = Math.max(
      0,
      calculatedCharges - calculatedDiscounts - calculatedPaid
    );

    return {
      totalCharges: calculatedCharges,
      totalDiscounts: calculatedDiscounts,
      remainingAmount: remaining,
    };
  }, [pricesRes]);

  return (
    <div>
      <BreadcrumbLayout.Paper>
        {pricesRes ? (
          <Box>
            <AppTable
              columns={columns}
              data={Array.isArray(pricesRes?.data) ? pricesRes.data : []}
              progressPending={isLoading || isFetching}
              progressComponent={
                <Box display="flex" alignItems="center" padding={2}>
                  <CircularProgress size={24} />
                  <Typography variant="body2" marginLeft={1}>
                    Loading Shipment Prices...
                  </Typography>
                </Box>
              }
              paginationServer
              paginationPerPage={perPage}
              paginationTotalRows={pricesRes?.totalItems ?? 0}
              paginationDefaultPage={currentPage}
              onChangePage={handlePageChange}
              onChangeRowsPerPage={handlePerRowsChange}
              noDataComponent={
                <Typography variant="body2" padding={2}>
                  No shipment prices found.
                </Typography>
              }
            />
            {/* Professional Invoice Summary Section */}
            <Card
              sx={{
                mt: 0,
                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                borderRadius: 1,
                border: "1px solid",
                borderColor: "grey.200",
              }}
            >
              <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                <Typography
                  variant="subtitle1"
                  fontWeight="medium"
                  color="text.primary"
                  mb={1.5}
                >
                  Net Summary
                </Typography>
                <Divider sx={{ mb: 1.5, borderColor: "grey.300" }} />
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell
                        sx={{ py: 0.5, display: "flex", alignItems: "center" }}
                      >
                        <AttachMoney
                          sx={{ color: "grey.600", fontSize: 18, mr: 1 }}
                        />
                        <Typography
                          variant="body2"
                          color="text.primary"
                          fontWeight="medium"
                        >
                          Total Charges
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ py: 0.5 }}>
                        <Typography
                          variant="body2"
                          color="text.primary"
                          fontWeight="medium"
                        >
                          Rs. {(netSummary?.totalCharges ?? pricesRes?.totalCharges ?? 0).toFixed(2)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        sx={{ py: 0.5, display: "flex", alignItems: "center" }}
                      >
                        <Discount
                          sx={{ color: "grey.600", fontSize: 18, mr: 1 }}
                        />
                        <Typography
                          variant="body2"
                          color="text.primary"
                          fontWeight="medium"
                        >
                          Total Applied Discounts
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ py: 0.5 }}>
                        <Typography
                          variant="body2"
                          color="text.primary"
                          fontWeight="medium"
                        >
                          - Rs. {(netSummary?.totalDiscounts ?? pricesRes?.totalDiscounts ?? 0).toFixed(2)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        sx={{ py: 0.5, display: "flex", alignItems: "center" }}
                      >
                        <AccountBalance
                          sx={{ color: "grey.600", fontSize: 18, mr: 1 }}
                        />
                        <Typography
                          variant="subtitle2"
                          color="text.primary"
                          fontWeight="bold"
                        >
                          Remaining Amount
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ py: 0.5 }}>
                        <Typography
                          variant="subtitle2"
                          color="text.primary"
                          fontWeight="bold"
                        >
                          Rs.{" "}
                          {(netSummary?.remainingAmount ?? pricesRes?.remainingShipmentAmount ?? 0).toFixed(2)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </Box>
        ) : (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            padding={2}
          >
            <CircularProgress size={24} />
            <Typography variant="body2" marginLeft={1}>
              Loading Shipment Prices...
            </Typography>
          </Box>
        )}
      </BreadcrumbLayout.Paper>
    </div>
  );
};

export default ShipmentPricingList;
